import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
    BehaviorSubject,
    catchError,
    defer,
    distinctUntilChanged,
    EMPTY,
    filter,
    firstValueFrom,
    forkJoin,
    map,
    Observable,
    ReplaySubject,
    share,
    Subject,
    switchMap,
    take,
    takeUntil,
    tap,
    throwError,
    timeout,
    TimeoutError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { Company } from '../../models/company.model';
import { ReloginUser, User } from '../../models/user.model';
import { APIResponse } from '../api/api.service';
import { CompanyService } from '../company/company.service';
import { authSessionContext } from './auth-http.context';
import { StoredToken, TokenStorage } from './token.storage';

export const AUTH_SESSION_TIMEOUT_MS = new InjectionToken<number>('AUTH_SESSION_TIMEOUT_MS', {
    providedIn: 'root',
    factory: () => 15000,
});

interface StoredSession {
    credentials: StoredToken;
    company: Company | null;
}

interface SessionState {
    pending: boolean;
    user: User | null;
}
interface Session<T> {
    user: User;
    accessToken: string;
    company?: Company | null;
    result: T;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly location = inject(Location);
    private readonly router = inject(Router);
    private readonly sessionTimeoutMs = inject(AUTH_SESSION_TIMEOUT_MS);
    private readonly url = environment.apiBase;
    private readonly state = new BehaviorSubject<SessionState>({ pending: true, user: null });
    private readonly sessionChanged = new Subject<void>();
    private sessionVersion = 0;
    private initialized = false;
    private readonly initialTokens: string[];
    readonly loading = signal(false);
    readonly reloginError = signal<'invalid' | 'technical' | null>(null);

    constructor(
        private http: HttpClient,
        private tokenStorage: TokenStorage,
        private companyService: CompanyService
    ) {
        // Capture once, before the router's initial navigation or any restoration of the old session.
        const url = this.router.parseUrl(this.location.path(true) || '/');
        this.initialTokens = url.queryParamMap.getAll('token');
        if (this.initialTokens.length) {
            delete url.queryParams['token'];
            this.location.replaceState(this.router.serializeUrl(url));
            this.loading.set(true);
        }
    }

    /** Pending transitions emit null so subscribers stop work for the previous session. */
    get user$(): Observable<User | null> {
        this.initialize();
        return this.state.pipe(
            map((state) => state.user),
            distinctUntilChanged()
        );
    }

    /** Guards and one-shot readers must wait for the complete session. */
    get readyUser$(): Observable<User | null> {
        this.initialize();
        return this.state.pipe(
            filter((state) => !state.pending),
            map((state) => state.user)
        );
    }

    initialize(): void {
        if (this.initialized) return;
        this.initialized = true;
        if (this.initialTokens.length) {
            if (this.initialTokens.length !== 1 || !this.initialTokens[0].trim()) {
                this.signOut();
                this.reloginError.set('invalid');
                return;
            }
            this.loginByToken(this.initialTokens[0]).subscribe({ error: () => undefined });
            return;
        }

        const saved = this.storedSession();
        if (!saved) {
            this.signOut();
            return;
        }
        this.runSession(() => this.restoreSession$(saved)).subscribe({ error: () => undefined });
    }

    login(email: string, password: string, type: string): Observable<User> {
        return this.runSession(() =>
            this.http
                .post<
                    APIResponse<User>
                >(`${this.url}/login`, { email, password, type }, { context: authSessionContext() })
                .pipe(
                    map((response) => this.loginData(response)),
                    switchMap((data) =>
                        this.me$(data.accessToken!).pipe(
                            map((user) => {
                                this.assertSameUser(data, user);
                                return { user, accessToken: data.accessToken!, result: user };
                            })
                        )
                    )
                )
        );
    }

    loginByToken(reloginToken: string): Observable<User> {
        return this.runSession(
            () =>
                this.http
                    .get<
                        APIResponse<ReloginUser>
                    >(`${this.url}/persons/relogin/${encodeURIComponent(reloginToken)}`, { context: authSessionContext() })
                    .pipe(
                        map((response) => {
                            const data = this.loginData(response);
                            if (!this.hasId(data.companyId)) throw new Error('Missing relogin company');
                            return data;
                        }),
                        switchMap((data) =>
                            forkJoin({
                                user: this.me$(data.accessToken!),
                                company: this.http
                                    .get<
                                        APIResponse<Company>
                                    >(`${this.url}/companies/${encodeURIComponent(data.companyId)}`, { context: authSessionContext(data.accessToken!, data.companyId) })
                                    .pipe(map((response) => this.responseData(response))),
                            }).pipe(
                                map(({ user, company }) => {
                                    this.assertSameUser(data, user);
                                    if (String(company.id) !== String(data.companyId))
                                        throw new Error('Unexpected relogin company');
                                    return {
                                        user,
                                        company,
                                        accessToken: data.accessToken!,
                                        result: { ...user, companyId: data.companyId },
                                    };
                                })
                            )
                        )
                    ),
            true
        );
    }

    private runSession(load: () => Observable<Session<User>>, relogin = false): Observable<User> {
        return defer(() => {
            // Keep only this attempt's snapshot: storage is cleared while the new identity is pending.
            const saved = relogin ? this.storedSession() : null;
            const version = ++this.sessionVersion;
            this.initialized = true;
            this.sessionChanged.next();
            this.reloginError.set(null);
            this.loading.set(true);
            this.state.next({ pending: true, user: null });
            this.tokenStorage.signOut();
            this.companyService.reset();

            return defer(load).pipe(
                timeout({ first: this.sessionTimeoutMs }),
                catchError((error: unknown) => {
                    if (!(error instanceof TimeoutError) || !saved) return throwError(() => error);
                    // One bounded recovery, never another token exchange or a recursive retry.
                    return this.restoreSession$(saved).pipe(timeout({ first: this.sessionTimeoutMs }));
                }),
                take(1),
                takeUntil(this.sessionChanged),
                filter(() => version === this.sessionVersion),
                tap((session) => {
                    this.tokenStorage.saveToken(session.accessToken, session.user.id);
                    if (session.company) this.companyService.storageActive = session.company;
                    this.loading.set(false);
                    this.state.next({ pending: false, user: session.user });
                }),
                map((session) => session.result),
                catchError((error: unknown) => {
                    if (version === this.sessionVersion) {
                        this.signOut();
                        if (relogin) {
                            this.reloginError.set(
                                error instanceof TimeoutError ||
                                    (error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500))
                                    ? 'technical'
                                    : 'invalid'
                            );
                        }
                    }
                    return throwError(() => error);
                })
            );
        }).pipe(
            share({
                connector: () => new ReplaySubject<User>(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false,
            })
        );
    }

    private storedSession(): StoredSession | null {
        const credentials = this.tokenStorage.getTokenData();
        return credentials ? { credentials, company: this.companyService.storageActive } : null;
    }

    private restoreSession$({ credentials, company }: StoredSession): Observable<Session<User>> {
        return this.me$(credentials.token).pipe(
            map((user) => {
                if (String(user.id) !== String(credentials.id)) throw new Error('Unexpected session user');
                return { user, accessToken: credentials.token, company, result: user };
            })
        );
    }

    signOut(): void {
        ++this.sessionVersion;
        this.initialized = true;
        this.sessionChanged.next();
        this.state.next({ pending: true, user: null });
        this.tokenStorage.signOut();
        this.companyService.reset();
        this.loading.set(false);
        this.reloginError.set(null);
        this.state.next({ pending: false, user: null });
    }

    getReloginToken(): Observable<string> {
        return this.http
            .get<APIResponse<{ reloginToken: string }>>(`${this.url}/persons/relogin`)
            .pipe(map((response) => response.data!.reloginToken));
    }

    generateOnetimePassword(): Promise<string> {
        return firstValueFrom(
            this.http
                .post<APIResponse<{ onetimePassword: string }>>(`${this.url}/onetime-password`, [])
                .pipe(map((response) => response.data!.onetimePassword))
        );
    }

    getAuthorizationHeaders() {
        return { Authorization: `Bearer ${this.getBearerToken()}` };
    }

    getBearerToken(): string | null {
        return this.tokenStorage.getTokenForHeader() || '';
    }

    user(): Promise<User | null> {
        return firstValueFrom(this.readyUser$);
    }

    private me$(token: string): Observable<User> {
        return this.http
            .get<
                APIResponse<User>
            >(`${this.url}/persons/me?load[]=companies&companies.has-devices=true`, { context: authSessionContext(token) })
            .pipe(map((response) => this.responseData(response)));
    }

    private responseData<T extends { id: string }>(response: APIResponse<T>): T {
        if (response.success === false || !response.data || !this.hasId(response.data.id)) {
            throw new Error('Invalid authentication response');
        }
        return response.data;
    }

    private loginData<T extends User>(response: APIResponse<T>): T {
        const data = this.responseData(response);
        if (typeof data.accessToken !== 'string' || !data.accessToken.trim()) {
            throw new Error('Missing access token');
        }
        return data;
    }

    private hasId(id: unknown): boolean {
        return (typeof id === 'string' && !!id.trim()) || (typeof id === 'number' && Number.isFinite(id));
    }

    private assertSameUser(login: User, user: User): void {
        if (String(login.id) !== String(user.id)) throw new Error('Unexpected session user');
    }

    updateIsMarketingFlag(isMarketing: boolean): Observable<User> {
        return defer(() => {
            const version = this.sessionVersion;
            if (this.state.value.pending || !this.state.value.user) return EMPTY;
            return this.http.put<APIResponse<User>>(`${this.url}/persons/me`, { isMarketing }).pipe(
                takeUntil(this.sessionChanged),
                map((response) => this.responseData(response)),
                filter(() => version === this.sessionVersion),
                tap((user) => this.state.next({ pending: false, user }))
            );
        });
    }
}
